import { useInfiniteQuery } from "@tanstack/react-query";
import { FitnessClass, Filters } from "@/types/fitness-class";

interface UseClassesWithFiltersParams {
  limit: number;
  sortBy: string;
  sortOrder: string;
  filters: Filters;
}

interface FetchClassesResponse {
  classes: FitnessClass[];
  pagination: {
    total: number;
    hasNext: boolean;
    currentPage: number;
  };
}

async function fetchClassesWithFilters({
  page,
  limit,
  sortBy,
  sortOrder,
  filters,
}: UseClassesWithFiltersParams & {
  page: number;
}): Promise<FetchClassesResponse> {
  const queryParams = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sortBy,
    sortOrder,
  });

  // Add filters to query params
  Object.entries(filters).forEach(([key, value]) => {
    // Skip status key to fetch all statuses as requested
    if (key === "status") return;

    if (value !== "" && value !== null && value !== undefined) {
      // Map 'instructor' filter to 'instructorId' for API
      const apiKey = key === "instructor" ? "instructorId" : key;

      if (Array.isArray(value)) {
        if (value.length > 0) {
          queryParams.append(apiKey, value.join(","));
        }
      } else {
        queryParams.append(apiKey, String(value));
      }
    }
  });

  const response = await fetch(`/api/classes?${queryParams.toString()}`);

  if (!response.ok) {
    throw new Error("Failed to fetch classes");
  }

  const data = await response.json();
  return {
    classes: data.classes || [],
    pagination: {
      total: data.pagination?.total || 0,
      hasNext: data.pagination?.hasNext || false,
      currentPage: page,
    },
  };
}

export function useClassesWithFilters(params: UseClassesWithFiltersParams) {
  return useInfiniteQuery<FetchClassesResponse, Error>({
    queryKey: ["classes", params],
    queryFn: ({ pageParam = 1 }) =>
      fetchClassesWithFilters({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasNext
        ? lastPage.pagination.currentPage + 1
        : undefined;
    },
    initialPageParam: 1,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
